import React from "react"
import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormRegister, UseFormTrigger, UseFormWatch } from "react-hook-form";
import moment from "moment";
import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { integerRegex, upToTenThousandRegex } from "../../../../shared/validators";
import { type ChangeMachine, type Machine, type CustomerMachineryState } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";


export interface ProductionLineFormProps {
  machine?: Machine;
  control: Control<ChangeMachine>;
  register: UseFormRegister<ChangeMachine>;
  errors: FieldErrors<ChangeMachine>;
  watch: UseFormWatch<ChangeMachine>;
  trigger: UseFormTrigger<ChangeMachine>;
  clearErrors: UseFormClearErrors<ChangeMachine>;
}

const ProductionLineDialog = ({ machine, control, register, errors, watch, trigger, clearErrors }: ProductionLineFormProps) => {
  const { manufacturers, operationTypes, controlSystemBrands, controlSystemModels, automationLevels } = useAppSelector<CustomerMachineryState>(
    (state: any) => state.customerMachinery
  );
  const { translate: tCommon } = useTranslation("common");
  const { translate } = useTranslation("myMachinery");
  const controlSystemBrandId = watch("controlSystemBrandId");

  const stylesPlaceholder = {
    "input": {
      "&::placeholder": {
        opacity: 1
      }
    }
  };

  return (<Box>
    <Box className="mt-5">
      <InputLabel required>{tCommon("brand")}</InputLabel>
      <Controller
        name="manufacturerId"
        defaultValue=""
        control={control}
        rules={{ required: tCommon("forms.required") }}
        render={({ field }) => (
          <Autocomplete
            options={manufacturers}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={manufacturers.find(m => m.id === field.value) || null}
            onChange={(_, selected) => {
              field.onChange(selected?.id || "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!errors.manufacturerId}
                helperText={errors.manufacturerId?.message}
                placeholder={translate("manufacturer")}
              />
            )}
          />
        )}
      />
    </Box>

    <Box className="mt-5">
      <InputLabel required>{translate("lineNameModel")}</InputLabel>
      <TextField
        className="w-full"
        defaultValue={machine?.model || ""}
        {...register("model", { required: tCommon("forms.required") })}
        placeholder={translate("modelPlaceholder")}
        inputProps={{ maxLength: 50 }}
        error={!!errors.model}
        helperText={errors.model?.message}
      ></TextField>
    </Box>

    <Box className="mt-5">
      <InputLabel required>{translate("operationType")}</InputLabel>
      <FormControl className="w-full">
        <Select
          displayEmpty
          defaultValue={machine?.operationType?.id || ""}
          onClose={() => setTimeout(() => trigger("operationTypeId"))}
          {...register("operationTypeId", { required: tCommon("forms.required") })}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 250,
                overflow: 'auto'
              }
            }
          }}
          renderValue={(selected) => {
            if (!selected) {
              return <Box>{translate("chooseFromList")}</Box>;
            }

            return operationTypes.find((x) => x.id === selected)?.name;
          }}
          error={!!errors.operationTypeId}
        >
          {operationTypes?.map((operationType) => (
            <MenuItem key={operationType.id} value={operationType.id}>
              {operationType.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>

    <Box className="grid grid-cols-2 gap-4 mt-5">
      <Box>
        <InputLabel required>{translate("noOfStations")}</InputLabel>
        <TextField
          className="w-full"
          defaultValue={machine?.numberOfStations || ""}
          {...register("numberOfStations", {
            pattern: { value: upToTenThousandRegex, message: translate("stationsRange") },
            required: tCommon("forms.required")
          })}
          inputProps={{ maxLength: 10 }}
          placeholder={translate("serialNumberPlaceholder")}
          error={!!errors.numberOfStations}
          helperText={errors.numberOfStations?.message}
        ></TextField>
      </Box>

      <Box>
        <InputLabel required>{translate("automationLevel")}</InputLabel>
        <FormControl className="w-full">
          <Select
            displayEmpty
            defaultValue={machine?.automationLevel?.id || ""}
            onClose={() => setTimeout(() => trigger("automationLevelId"))}
            {...register("automationLevelId", { required: tCommon("forms.required") })}
            renderValue={(selected) => {
              if (!selected) {
                return <Box>{translate("chooseFromList")}</Box>;
              }

              return automationLevels.find((x) => x.id === selected)?.name;
            }}
            error={!!errors.automationLevelId}
          >
            {automationLevels?.map((automationLevel) => (
              <MenuItem key={automationLevel.id} value={automationLevel.id}>
                {automationLevel.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <InputLabel required>{translate("serialNumber")}</InputLabel>
        <TextField
          className="w-full"
          defaultValue={machine?.serialNumber || ""}
          placeholder={translate("serialNumberPlaceholder")}
          {...register("serialNumber", { required: tCommon("forms.required") })}
          inputProps={{ maxLength: 50 }}
          error={!!errors.serialNumber}
          helperText={errors.serialNumber?.message}
        ></TextField>
      </Box>

      <Box>
        <InputLabel>{translate("productionYear")}</InputLabel>
        <TextField
          className="w-full"
          placeholder={moment().year().toString()}
          {...register("productionYear", {
            pattern: { value: integerRegex, message: translate("fieldRequire4Digits") },
            min: { value: 1900, message: translate("enterValidYearRange", { year: moment().year() }) },
            max: { value: moment().year(), message: translate("enterValidYearRange", { year: moment().year() }) }
          })}
          inputProps={{ maxLength: 4 }}
          error={!!errors.productionYear}
          helperText={errors.productionYear?.message}
        />
      </Box>

      <Box>
        <InputLabel required>{translate("controlSystemBrand")}</InputLabel>
        <FormControl className="w-full">
          <Select
            displayEmpty
            defaultValue={machine?.controlSystemBrand?.id || ""}
            onClose={() => setTimeout(() => trigger("controlSystemBrandId"))}
            {...register("controlSystemBrandId", { required: tCommon("forms.required") })}
            MenuProps={{
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              PaperProps: {
                style: {
                  maxHeight: 170,
                  overflow: 'auto'
                }
              }
            }}
            renderValue={(selected) => {
              if (!selected) {
                return <Box>{translate("chooseFromList")}</Box>;
              }

              return controlSystemBrands.find((x) => x.id === selected)?.name;
            }}
            error={!!errors.controlSystemBrandId}
          >
            {controlSystemBrands?.map((controlSystemBrand) => (
              <MenuItem key={controlSystemBrand.id} value={controlSystemBrand.id}>
                {controlSystemBrand.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <InputLabel required>{translate("controlSystemModel")}</InputLabel>
        <Autocomplete
          freeSolo
          disabled={controlSystemBrandId === ""}
          options={controlSystemModels.map((m) => m.name)}
          getOptionLabel={(option) => option}
          defaultValue={machine?.system}
          ListboxProps={{ style: { maxHeight: "20vh" } }}
          onChange={() => clearErrors()}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              sx={stylesPlaceholder}
              placeholder={translate("typeOrChoose")}
              inputProps={{
                ...params.inputProps
              }}
              {...register("system", { required: true })}
              error={!!errors.system}
              helperText={errors.system ? tCommon("forms.required") : ""}
            />
          )}
        />
      </Box>
    </Box>
  </Box>);
};

export default ProductionLineDialog;